<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTenantRolePermissionsStore } from '@/@vxapp/TenantRolePermissions/useTenantRolePermissionsStore'
import type {
  TenantRolePermission,
  TenantRolePermissionWritable,
} from '@/@vxapp/TenantRolePermissions/useTenantRolePermissionsStore'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { canPageMeta } from '@/@layouts/plugins/casl'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import { useAccessContext, inaccessible } from '@/access/useAccessContext'
import TenantRolePermissionForm from '@/components/Forms/TenantRolePermissions/TenantRolePermissionForm.vue'

definePage({ meta: PageMeta.TenantRolePermissions.Preview.Meta })
setPageTitle('Role permissions details')

const store = useTenantRolePermissionsStore()
const route = useRoute('setup-tenant-role-permissions-view-guid')
const router = useRouter()
const selected = ref<TenantRolePermission | null>(null)
const editing = ref(false)
const loading = ref(false)
const error = ref('')
const errors = ref<unknown>()
const denied = ref(false)

const fields = [
  'permissionGUID',
  'roleGUID',
  'definitionScope',
  'permissionDefinitionGUID',
  'enabled',
  'createdAt',
  'updatedAt',
] as const

let request = 0

const context = useAccessContext(() => {
  request += 1
  selected.value = null
  editing.value = false
  error.value = ''
  errors.value = undefined
  loading.value = false
  denied.value = false
}, '/setup/tenant-role-permissions/list')

async function load() {
  selected.value = null
  editing.value = false
  error.value = ''
  denied.value = false

  const identifier = typeof route.params.guid === 'string' ? route.params.guid : ''
  const version = ++request
  const epoch = context.epoch()
  if (!identifier) {
    denied.value = true

    return
  }
  loading.value = true

  const result = await store.fetch(identifier)
  if (!context.isCurrent(epoch) || version !== request) return
  loading.value = false
  if (result.responseCode !== 200 || !('tenantRolePermission' in result)) {
    denied.value = inaccessible(result.responseCode)
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(result.tenantRolePermission)
}
function edit() {
  if (canPageMeta(PageMeta.TenantRolePermissions.Edit.Meta)) editing.value = true
}
function cancel() {
  editing.value = false
  error.value = ''
  errors.value = undefined
}
function back() {
  void router.push({ name: PageMeta.TenantRolePermissions.List.Name })
}
async function save(payload: TenantRolePermissionWritable) {
  const identifier = selected.value?.permissionGUID
  if (
    !identifier ||
    loading.value ||
    !canPageMeta({ action: A.UPDATE, subject: Subject.TENANT_ROLE_PERMISSIONS_UPDATE_GUID })
  )
    return
  const epoch = context.epoch()
  const version = request

  loading.value = true

  const result = await store.update(identifier, payload)
  if (!context.isCurrent(epoch) || version !== request) return
  loading.value = false
  if (result.responseCode !== 200 || !('tenantRolePermission' in result)) {
    error.value = result.message
    errors.value = result.errors
    if (inaccessible(result.responseCode)) {
      selected.value = null
      editing.value = false
      denied.value = true
    }

    return
  }
  selected.value = cloneReactiveData(result.tenantRolePermission)
  cancel()
}
watch(() => route.params.guid, load, { immediate: true })
</script>

<template>
  <main>
    <h1 class="mb-4">Role permissions details</h1>
    <VBtn
      class="mb-4"
      @click="back"
      >Back to list</VBtn
    >
    <VProgressLinear
      v-if="loading"
      indeterminate
    />
    <VAlert
      v-if="denied"
      type="error"
      data-testid="tenant-context-unavailable"
      >This resource is inaccessible in the current context.</VAlert
    >
    <VAlert
      v-else-if="error"
      type="error"
      >{{ error }}</VAlert
    >
    <VCard
      v-if="selected"
      class="mt-4"
      data-testid="tenant-role-permission-details"
    >
      <VCardText>
        <TenantRolePermissionForm
          v-if="editing"
          :permission="selected"
          is-edit
          :loading="loading"
          :errors="errors"
          @submit="save"
          @cancel="cancel"
        />
        <template v-else>
          <dl>
            <template
              v-for="field in fields"
              :key="field"
              ><dt class="font-weight-bold">{{ field }}</dt>
              <dd class="mb-3">{{ selected[field] ?? '—' }}</dd></template
            >
          </dl>
          <VBtn
            v-if="canPageMeta(PageMeta.TenantRolePermissions.Edit.Meta)"
            @click="edit"
            >Edit permission</VBtn
          >
        </template>
      </VCardText>
    </VCard>
  </main>
</template>
